import React, {
  forwardRef,
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Controller, useFormContext } from 'react-hook-form'
import { isEmpty, last, map, orderBy } from 'lodash'
import {
  Eye as EyeIcon,
  Info as InfoIcon,
  Trash2 as Trash2Icon,
  Calendar as CalendarIcon,
  FilePlus as FilePlusIcon,
} from 'react-feather'
import {
  Box,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { EvidenceAnalysisDai } from '../EvidenceQuestion/components'
import { DocumentTemplateList } from './components'
import { DocumentListShortcut, LinkedDocumentList } from 'components'
import { EvidenceQuestion } from '..'

import helpers from 'helpers'

import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import useStyles from './styles'
import * as service from 'service'

const EvidencePlanAction = forwardRef(
  (
    {
      activity,
      evidencePlanAction,
      defaultEvidencePlanAction,
      users,
      governanceProjectId,
      disabledAll,
      canEditActivity,
      planActionsOpened,
      setLoading,
      setStates,
      componentIndex,
      removeEvidencePlanAction,
      isCreatedManually = false,
    },
    ref,
  ) => {
    const evidence = evidencePlanAction?.evidence
    const snackbar = useSnackbar()
    const classes = useStyles()
    const { user } = useAuth()

    const { handleRemove } = helpers.file

    const { control, errors, setValue, watch } = useFormContext()
    const name = `evidencePlanActions[${componentIndex}]`
    const attachments = watch(`${name}.evidence.attachments`)
    const documents = watch(`${name}.evidence.privacyPolicyIds`)

    const componentError = errors?.evidencePlanActions?.[componentIndex]

    const [existingAttachments, setExistingAttachments] = useState(
      defaultEvidencePlanAction?.evidence?.attachments ?? [],
    )
    const [existingLinkedDocuments, setExistingLinkedDocuments] = useState(
      defaultEvidencePlanAction?.evidence?.linkedDocuments ?? [],
    )

    const currentAttachments = useMemo(() => {
      return [...(attachments ?? []), ...existingAttachments]
    }, [attachments, existingAttachments])

    const currentLinkedDocuments = useMemo(() => {
      return [...(documents ?? []), ...existingLinkedDocuments]
    }, [documents, existingLinkedDocuments])

    const excludedIds = useMemo(
      () =>
        currentLinkedDocuments.map(
          (d) => d.documentVersion?.privacyPolicyId || d.id,
        ),
      [currentLinkedDocuments],
    )

    const allItems = useMemo(() => {
      const attachmentItems = currentAttachments.map((a, idx) => ({
        ...a,
        name: a?.name,
        _type: 'attachment',
        _formIndex: idx,
      }))
      const documentItems = currentLinkedDocuments.map((d) => ({
        ...d,
        _type: 'document',
      }))
      return orderBy(
        [...attachmentItems, ...documentItems],
        [
          (i) =>
            i?.name?.toLowerCase() || i?.documentVersion?.title?.toLowerCase(),
        ],
        ['asc'],
      )
    }, [currentAttachments, currentLinkedDocuments])

    const evidenceDisabled = useMemo(() => {
      return map(
        evidencePlanAction?.planActions,
        (planAction) => planAction?.responsible?.id,
      ).includes(user?.id)
    }, [evidencePlanAction?.planActions, user?.id])

    useEffect(() => {
      setValue(name, evidencePlanAction)

      //eslint-disable-next-line
    }, [name])

    const disabledInputs =
      (!evidenceDisabled && !canEditActivity) || disabledAll

    const handleOpenUrl = (url) => window.open(url, '_blank')

    const isItemPersisted = (item) =>
      item._type === 'document' ? !!item.documentVersion?.id : !!item.id

    // O vínculo criado pelo card de modelos de documento já nasce persistido, então entra na mesma
    // lista dos documentos que vieram da API — sem passar pelo formulário e sem recarregar a
    // atividade, o que descartaria as alterações ainda não salvas.
    const handleLinkedDocumentTemplateLink = (linkedDocuments) =>
      setExistingLinkedDocuments((prev) => [...prev, ...linkedDocuments])

    const handleRemovePlatformDocument = (documentId) => {
      setValue(
        `${name}.evidence.privacyPolicyIds`,
        (documents ?? []).filter((d) => d.id !== documentId),
      )
    }

    const handleLinkedDocumentRemove = async (item) => {
      if (item._type === 'document') {
        if (item.documentVersion?.id) {
          try {
            setLoading(true)
            await service.dponet.governanceProject.removeEvidencePlatformDocument(
              {
                governanceProjectId,
                evidencePlanActionId: evidencePlanAction?.id,
                documentId: item.id,
              },
            )
            setExistingLinkedDocuments((prev) =>
              prev?.filter((d) => d?.id !== item.id),
            )
            snackbar.open({
              message: 'Documento removido com sucesso!',
              variant: 'success',
            })
          } catch (error) {
            snackbar.open({
              message: helpers.formatters.errorMessage(
                error?.response?.data?.error,
              ),
              variant: 'error',
            })
          } finally {
            setLoading(false)
          }
        } else {
          handleRemovePlatformDocument(item.id)
        }
        return
      }

      if (item.id) {
        try {
          setLoading(true)
          await service.dponet.governanceProject.removeEvidenceDocument({
            governanceProjectId,
            evidencePlanActionId: evidencePlanAction?.id,
            documentId: item.id,
          })
          setExistingAttachments((prev) =>
            prev?.filter((attachment) => attachment?.id !== item.id),
          )
          snackbar.open({
            message: 'Documento removido com sucesso!',
            variant: 'success',
          })
        } catch (error) {
          snackbar.open({
            message: helpers.formatters.errorMessage(
              error?.response?.data?.error,
            ),
            variant: 'error',
          })
        } finally {
          setLoading(false)
        }
      } else {
        handleRemove({
          attachments,
          controlName: `${name}.evidence.attachments`,
          index: item._formIndex,
          setValue,
        })
      }
    }

    return (
      // O id é a âncora usada pela listagem de controles do documento para trazer o usuário direto a
      // esta evidência (ver helpers.governanceProject.mountEvidenceAnchor).
      <Paper
        variant="outlined"
        className={classes.sectionPaper}
        id={helpers.governanceProject.mountEvidenceAnchor(
          defaultEvidencePlanAction?.evidence?.id,
        )}
        ref={ref}
      >
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
          <Box display="flex" alignItems="center" gridGap={theme.spacing(2)}>
            <Controller
              as={
                isCreatedManually ? (
                  <TextField
                    label="Nome da Evidência"
                    disabled={disabledInputs}
                    variant="outlined"
                    error={!!componentError?.evidence?.title}
                    helperText={componentError?.evidence?.title?.message}
                    className={classes.whiteInput}
                    fullWidth
                  />
                ) : (
                  <Typography variant="h6" color="secondary">
                    {`${componentIndex + 1}. ${evidence?.title}`}
                  </Typography>
                )
              }
              name={`${name}.evidence.title`}
              control={control}
            />
            {isCreatedManually && (
              <IconButton
                onClick={removeEvidencePlanAction}
                disabled={disabledInputs}
              >
                <Trash2Icon />
              </IconButton>
            )}
          </Box>
          <Controller
            as={
              isCreatedManually ? (
                <TextField
                  label="Descrição da Evidência"
                  disabled={disabledInputs}
                  variant="outlined"
                  multiline
                  minRows={4}
                  maxRows={18}
                  error={!!componentError?.evidence?.description}
                  helperText={componentError?.evidence?.description?.message}
                  className={classes.whiteInput}
                  fullWidth
                />
              ) : evidence?.description ? (
                <Typography variant="body1" color="secondary" gutterBottom>
                  {evidence?.description}
                </Typography>
              ) : (
                <></>
              )
            }
            name={`${name}.evidence.description`}
            control={control}
          />
        </Box>
        {!isEmpty(currentAttachments) && !!evidence?.updatedAt && (
          <Box className={classes.evidenceDate}>
            <CalendarIcon size={20} />
            <Typography variant="h5">Evidência enviada em:</Typography>
            <Typography variant="h6">
              {helpers.formatters.dates.ptBrFormatDateTime(evidence?.updatedAt)}
            </Typography>
          </Box>
        )}
        <Controller
          as={<Fragment />}
          name={`${name}.originId`}
          control={control}
        />
        {/* Campo sem UI: o schema de validação decide por ele se nome e descrição são obrigatórios, e o
            envio decide se eles vão no payload. Sem registrar aqui, o valor não chega em nenhum dos dois
            — o react-hook-form só monta os dados dos campos registrados. O valor vem dos defaultValues
            do formulário, como os demais campos. */}
        <Controller
          as={<Fragment />}
          name={`${name}.createdManually`}
          control={control}
        />
        <Controller
          as={<Fragment />}
          name={`${name}.evidence.attachments`}
          control={control}
        />
        <Controller
          as={<Fragment />}
          name={`${name}.evidence.privacyPolicyIds`}
          control={control}
        />
        <Controller
          as={
            <TextField
              label="Justificativa / Comentários"
              className={classes.whiteInput}
              disabled={disabledInputs}
              variant="outlined"
              error={!!componentError?.evidence?.justify}
              helperText={componentError?.evidence?.justify?.message}
              multiline
              minRows={4}
              maxRows={18}
              placeholder="Utilize este espaço para explicar ou comentar a evidência. As informações inseridas aqui serão exibidas posteriormente no relatório."
              fullWidth
            />
          }
          name={`${name}.evidence.justify`}
          control={control}
        />

        {!isEmpty(allItems) && (
          <LinkedDocumentList
            items={allItems}
            disabledInputs={disabledInputs}
            onRemove={handleLinkedDocumentRemove}
            requiresConfirmation={isItemPersisted}
            showDocumentId
            showPendingLabel
            renderItemActions={(item) =>
              item._type === 'attachment' ? (
                <EvidenceAnalysisDai daiResponse={item.daiResponse} />
              ) : (
                <Fragment>
                  <EvidenceAnalysisDai daiResponse={item.daiResponse} />
                  <DocumentListShortcut
                    privacyPolicyId={item.documentVersion?.privacyPolicyId}
                  />
                </Fragment>
              )
            }
          />
        )}
        {/* Documentos modelos */}
        {!isEmpty(evidence?.modelDocuments) && (
          <Fragment>
            <Box
              display="flex"
              alignItems="center"
              gridGap={theme.spacing(1)}
              justifyContent="space-between"
            >
              <Typography variant="h6">Documentos de referência</Typography>
              <Tooltip
                title="Documentos de referência são modelos ou guias que podem ser utilizados como base para a elaboração da evidência."
                arrow
              >
                <InfoIcon size={16} cursor="help" />
              </Tooltip>
            </Box>
            <Paper variant="outlined">
              <PerfectScrollbar
                options={{
                  wheelPropagation: true,
                  useBothWheelAxes: false,
                }}
                className={classes.attachmentItems}
              >
                {evidence?.modelDocuments?.map((modelDocument, index, arr) => (
                  <Box key={index}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      px={2}
                      py={1}
                    >
                      <Box
                        display="flex"
                        alignContent="center"
                        gridGap={theme.spacing(1)}
                      >
                        <FilePlusIcon size={20} />
                        <Typography>{modelDocument?.name}</Typography>
                      </Box>
                      <Box>
                        <IconButton
                          variant="contained"
                          onClick={() => handleOpenUrl(modelDocument?.url)}
                        >
                          <EyeIcon size={20} />
                        </IconButton>
                      </Box>
                    </Box>
                    {modelDocument !== last(arr) && <Divider />}
                  </Box>
                ))}
              </PerfectScrollbar>
            </Paper>
          </Fragment>
        )}
        {/* Modelos de documento vinculados à evidência do template */}
        <DocumentTemplateList
          documentTemplates={
            defaultEvidencePlanAction?.evidence?.linkedDocumentTemplates
          }
          evidenceId={defaultEvidencePlanAction?.evidence?.id}
          governanceProjectId={governanceProjectId}
          activityId={activity?.id}
          linkedPrivacyPolicyIds={excludedIds}
          disabledInputs={disabledInputs}
          onLink={handleLinkedDocumentTemplateLink}
        />
        <EvidenceQuestion
          componentIndex={componentIndex}
          evidencePlanAction={defaultEvidencePlanAction}
          evidencePlanActionId={defaultEvidencePlanAction?.id}
          governanceProject={activity?.governanceProject}
          governanceProjectId={governanceProjectId}
          disabledInputs={disabledInputs}
          planActionsOpened={planActionsOpened}
          setStates={setStates}
          users={users}
          activity={activity}
          hasAnswer={!!activity?.governanceProjectQuestion?.answer}
          excludedIds={excludedIds}
        />
      </Paper>
    )
  },
)

export default EvidencePlanAction
